<template>
    <main
        v-if="data"
        class="relative overflow-hidden pb-10 pt-25 md:pb-15 md:pt-36">
        <TheBreadcrumbs :items="breadcrumbs" />

        <section
            class="container relative mt-5 flex flex-col justify-between gap-5 overflow-hidden rounded-20 p-3 md:gap-10 lg:flex-row lg:p-5">
            <div class="relative z-20 grow">
                <AtomTitle
                    level="1"
                    class="!text-20/120 md:!text-24/133 lg:!text-32/100">
                    {{ data?.data.name }}
                </AtomTitle>
                <div
                    class="mt-3 space-x-2.5 font-medium text-accent-100 md:mt-5">
                    <span
                        v-for="item in date"
                        :key="item">
                        {{ item }}
                    </span>
                </div>
                <div
                    v-if="data.data.description"
                    v-html="data.data.description"
                    class="tinyeditor mt-5 text-16/148 font-medium text-accent-100 md:mt-10" />
            </div>

            <picture
                class="relative z-20 aspect-video w-full shrink-0 grow lg:max-w-[50%]">
                <source
                    v-if="data.data.image_main?.[0].webp_url"
                    type="image/webp"
                    :srcset="
                        getMediaLink(
                            data.data.image_main?.[0].webp_url || '',
                            800
                        ) || ''
                    " />

                <source
                    v-else-if="
                        !data.data.image_main?.[0].webp_url &&
                        !data.data.image_main?.[0].url
                    "
                    type="image/webp"
                    srcset="/assets/images/ui/placeholder/webp/cromi.webp" />

                <img
                    :alt="data.data.image_main?.[0].alt || data.data.name"
                    :src="
                        getMediaLink(
                            data.data.image_main?.[0].url || '',
                            800
                        ) || '/assets/images/ui/placeholder/jpg-png/cromi.png'
                    "
                    class="aspect-video w-full shrink-0 grow rounded-12 object-cover" />
            </picture>

            <div class="absolute inset-0 z-10 size-full">
                <div class="bg-noise"></div>
            </div>
            <div
                class="absolute -bottom-[10rem] -left-[5rem] z-10 aspect-square w-[20rem] animate-slow-pulse rounded-full bg-accent/60 blur-[6rem] delay-300" />
            <div
                class="absolute -right-[5rem] -top-[10rem] z-10 aspect-square w-[20rem] animate-slow-pulse rounded-full bg-accent blur-[6rem] delay-500" />
            <div
                class="absolute -bottom-[5rem] left-1/2 z-10 aspect-square w-[10rem] -translate-x-1/2 animate-slow-pulse rounded-full bg-accent blur-[6rem] delay-500" />
        </section>

        <section
            v-if="
                data.data.image_optional?.length ||
                data.data.title_second_block ||
                data.data.additional_description
            "
            class="container relative mt-10 flex flex-col-reverse justify-between gap-5 md:mt-15 lg:flex-row lg:gap-10">
            <picture class="relative z-10 w-full grow lg:max-w-[50%]">
                <source
                    v-if="data.data.image_optional?.[0].webp_url"
                    type="image/webp"
                    :srcset="
                        getMediaLink(
                            data.data.image_optional[0].webp_url,
                            1000
                        ) || ''
                    " />

                <source
                    v-else-if="
                        !data.data.image_optional?.[0].webp_url &&
                        !data.data.image_optional?.[0].url
                    "
                    type="image/webp"
                    srcset="/assets/images/ui/placeholder/webp/cromi.webp" />

                <img
                    :alt="data.data.image_optional?.[0].alt || data.data.name"
                    :src="
                        getMediaLink(
                            data.data.image_optional?.[0].url || '',
                            1000
                        ) || 'assets/images/ui/placeholder/jpg-png/cromi.png'
                    "
                    class="aspect-video w-full rounded-12 object-cover" />
            </picture>

            <div
                class="relative z-10 grow space-y-3 md:space-y-5 lg:max-w-[50%]">
                <AtomTitle
                    v-if="data?.data.title_second_block"
                    level="2"
                    class="!text-20/120 md:!text-32/100">
                    {{ data?.data.title_second_block }}
                </AtomTitle>
                <div
                    v-if="data.data.additional_description"
                    v-html="data.data.additional_description"
                    class="tinyeditor text-16/148 font-medium text-accent-100" />
            </div>
            <div
                class="absolute bottom-[10%] left-1/2 h-[2.5rem] w-[80vw] -translate-x-1/2 animate-slow-pulse rounded-full bg-accent blur-[4rem]" />
        </section>

        <section
            v-if="data?.data.end_text"
            class="container mt-10 md:mt-15">
            <div
                v-html="data.data.end_text"
                class="tinyeditor mx-auto text-center text-16/148 font-medium text-ash-200 lg:w-3/4" />
        </section>

        <section class="container relative mt-10 md:mt-15">
            <div
                v-if="data.data.end_picture?.length"
                class="relative z-20">
                <div
                    class="mx-auto aspect-video overflow-hidden rounded-12 object-cover sm:w-5/6 md:w-3/4">
                    <picture class="size-full">
                        <source
                            v-if="data.data.end_picture?.[0].webp_url"
                            type="image/webp"
                            :srcset="
                                getMediaLink(
                                    data.data.end_picture[0].webp_url
                                ) || ''
                            " />

                        <source
                            v-else-if="
                                !data.data.end_picture?.[0].webp_url &&
                                !data.data.end_picture?.[0].url
                            "
                            type="image/webp"
                            srcset="
                                /assets/images/ui/placeholder/webp/cromi.webp
                            " />

                        <img
                            :alt="
                                data.data.end_picture[0].title ||
                                data.data.end_picture[0].alt ||
                                'not found image'
                            "
                            :src="
                                getMediaLink(
                                    data.data.end_picture?.[0].url || ''
                                ) ||
                                'assets/images/ui/placeholder/jpg-png/cromi.png'
                            " />
                    </picture>
                    <span
                        v-if="data.data.end_picture[0].alt"
                        v-html="data.data.end_picture[0].alt"
                        class="absolute bottom-0 rounded-bl-12 bg-accent bg-video-title p-2 font-medium text-ash-200 max-md:rounded-br-12 md:rounded-tr-12 md:p-3 md:text-16/120 md:font-semibold lg:px-5 lg:py-4 lg:text-20/120 xl:py-6 xl:text-24/133" />
                </div>
            </div>
            <div
                class="absolute left-1/2 top-1/2 size-full -translate-x-1/2 -translate-y-1/2">
                <PerspectiveWallSVG
                    class="absolute left-0 top-1/2 w-[8rem] -translate-y-1/2 sm:w-[12rem] md:w-[18rem] lg:w-[22rem] xl:w-[26rem]" />
                <PerspectiveWallSVG
                    class="absolute right-0 top-1/2 w-[8rem] -translate-y-1/2 -scale-x-100 sm:w-[12rem] md:w-[18rem] lg:w-[22rem] xl:w-[26rem]" />
                <div
                    class="absolute left-0 top-1/2 z-10 aspect-square w-[12rem] -translate-y-1/2 animate-slow-pulse rounded-full bg-accent blur-[6rem] delay-300 md:w-[15rem] lg:w-[20rem]" />
                <div
                    class="absolute right-0 top-1/2 z-10 aspect-square w-[12rem] -translate-y-1/2 animate-slow-pulse rounded-full bg-accent blur-[6rem] delay-500 md:w-[15rem] lg:w-[20rem]" />
            </div>
        </section>

        <PortfolioProducts
            v-if="data.data.products.data.length"
            :items="data.data.products.data"
            class="py-10 md:py-20" />
        <AtomHr />

        <TurnkeyImplementationSection
            :categories="data?.data.category?.data || ([] as Category[])"
            class="container py-10 md:py-20" />

        <AtomHr />
        <PortfolioSection
            v-if="data?.data.read_also?.data?.length"
            title="Другие проекты с похожим товаром"
            :items="data.data.read_also?.data"
            class="py-10 md:py-20" />
    </main>
</template>

<script setup lang="ts">
import type { Category } from '~/types/global.types'

import AtomHr from '~/components/atom/atom-hr/AtomHr.vue'
import AtomTitle from '~/components/atom/atom-title/AtomTitle.vue'
import TheBreadcrumbs, {
    type BreadcrumbItem,
} from '~/components/base/the-breadcrumbs/TheBreadcrumbs.vue'
import PerspectiveWallSVG from '~/components/pages/directions/invite-section/svg/PerspectiveWallSVG.vue'
import PortfolioProducts from '~/components/pages/portfolio/portfolio-products/PortfolioProducts.vue'
import TurnkeyImplementationSection from '~/components/pages/portfolio/turnkey-implementation-section/TurnkeyImplementationSection.vue'
import PortfolioSection from '~/components/particles/portfolio-section/PortfolioSection.vue'
import type { IPortfolioItem } from '~/components/particles/portfolio-section/portfolio-section.types'

const nuxtApp = useNuxtApp()
const modalStore = useModalStore()
const route = useRoute()

const { data, error } = await useApiFetch<Include<IPortfolioItem>>(
    `/v1/portfolios/${route.params.slug}`,
    {
        query: {
            include: [
                'city',
                'products',
                'blocks',
                'used_tools',
                'category',
                'read_also',
            ].join(','),
        },
        getCachedData(key) {
            return nuxtApp.payload?.data?.[key]
        },
        useLastModified: true,
    }
)

if (error.value) {
    throw createError(
        Object.assign(error.value, {
            message: error.value.statusMessage,
            statusMessage: 'error',
        })
    )
}

if (data.value?.data) {
    modalStore.setHeaderFeedbackLabel(data.value.data?.feedback_label || '')
    modalStore.setFeedbackLabel(data.value.data?.feedback_label || '')
} else {
    modalStore.setFeedbackLabel('')
}

const date = computed<string[]>(() => {
    if (!data.value?.data.started_at) return []

    const textDate: string = new Date(data.value?.data.started_at)
        .toLocaleDateString('ru-RU', {
            year: 'numeric',
            month: 'long',
        })
        .replace('г.', '')

    const [month, year]: string[] = textDate
        .replace(textDate[0], textDate[0].toUpperCase())
        .split(' ')
    const city: string = data.value?.data.city.data.name || ''

    return [city, month, year]
})

const breadcrumbs = computed<BreadcrumbItem[]>(() => [
    {
        label: 'Cromi',
        to: '/',
    },
    {
        label: `Портфолио`,
        to: `/portfolio`,
    },
    {
        label: `${data.value?.data.name}`,
        to: `/portfolio/${data.value?.data.slug}`,
    },
])

const organization = getSeoOrganizationReference()
const config = useRuntimeConfig()
if (data.value) {
    useSeoMeta({
        title: data.value.data.seo.seo_title,
        description: data.value.data.seo.seo_description,
        ogTitle: data.value.data.seo.og_title,
        ogDescription: data.value.data.seo.og_description,
        ogImage: data.value.data.seo.og_image
            ? data.value.data.seo.og_image
            : null,
    })
    const image = data.value.data.image_main
        ? data.value.data.image_main[0]
        : null
    useSchemaOrg([
        defineArticle({
            '@id': 'newsarticle',
            '@type': 'NewsArticle',
            headline: data.value.data.name,
            name: data.value.data.name,
            description: stripHtml(data.value.data.description),
            articleBody: data.value.data.blocks.data
                .map((block) => {
                    return stripHtml(block.text) || ''
                })
                .join(' '),
            datePublished: data.value.data.published_at,
            author: organization,
            publisher: organization,
            url: `https://www.${config.public.website}${route.path}`,
            ...(image && {
                image: getMediaLink(image.url),
                thumbnailUrl: getMediaLink(image.url),
            }),
        }),
    ])
}
</script>
